/**
 * Rate limiting utility for OTP requests
 * Prevents users from sending too many OTP requests in a short time period
 */

class RateLimiter {
  constructor() {
    this.attempts = new Map() // Store attempts per IP/contact
    this.lockouts = new Map() // Store lockout periods
  }

  /**
   * Check if a contact is rate limited
   * @param {string} contact - Email or phone number
   * @param {Object} options - Rate limiting options
   * @returns {Object} - { allowed: boolean, remainingAttempts: number, lockoutTime: number }
   */
  checkRateLimit(contact, options = {}) {
    const {
      maxAttempts = 3,
      windowMs = 15 * 60 * 1000, // 15 minutes
      lockoutMs = 30 * 60 * 1000, // 30 minutes
      maxLockouts = 2
    } = options

    const now = Date.now()
    const contactKey = contact.toLowerCase()

    // Check if contact is currently locked out
    const lockout = this.lockouts.get(contactKey)
    if (lockout && now < lockout.until) {
      return {
        allowed: false,
        remainingAttempts: 0,
        lockoutTime: lockout.until - now,
        reason: 'locked_out',
        message: `Too many failed attempts. Please try again in ${Math.ceil((lockout.until - now) / 60000)} minutes.`
      }
    }

    // Get or create attempts record
    let attempts = this.attempts.get(contactKey) || {
      count: 0,
      firstAttempt: now,
      lockoutCount: 0
    }

    // Reset attempts if window has passed
    if (now - attempts.firstAttempt > windowMs) {
      attempts = {
        count: 0,
        firstAttempt: now,
        lockoutCount: attempts.lockoutCount
      }
    }

    const remainingAttempts = Math.max(0, maxAttempts - attempts.count)

    return {
      allowed: attempts.count < maxAttempts,
      remainingAttempts,
      lockoutTime: 0,
      reason: attempts.count >= maxAttempts ? 'max_attempts_reached' : 'allowed',
      message: attempts.count >= maxAttempts 
        ? `Maximum attempts reached. Please try again in ${Math.ceil(windowMs / 60000)} minutes.`
        : null
    }
  }

  /**
   * Record a failed attempt
   * @param {string} contact - Email or phone number
   * @param {Object} options - Rate limiting options
   */
  recordFailedAttempt(contact, options = {}) {
    const {
      maxAttempts = 3,
      windowMs = 15 * 60 * 1000,
      lockoutMs = 30 * 60 * 1000,
      maxLockouts = 2
    } = options

    const now = Date.now()
    const contactKey = contact.toLowerCase()

    let attempts = this.attempts.get(contactKey) || {
      count: 0,
      firstAttempt: now,
      lockoutCount: 0
    }

    // Reset if window has passed
    if (now - attempts.firstAttempt > windowMs) {
      attempts = {
        count: 0,
        firstAttempt: now,
        lockoutCount: attempts.lockoutCount
      }
    }

    attempts.count++

    // If max attempts reached, start lockout
    if (attempts.count >= maxAttempts && attempts.lockoutCount < maxLockouts) {
      const lockoutUntil = now + lockoutMs
      this.lockouts.set(contactKey, {
        until: lockoutUntil,
        started: now
      })
      attempts.lockoutCount++
    }

    this.attempts.set(contactKey, attempts)
  }

  /**
   * Record a successful attempt (reset counters)
   * @param {string} contact - Email or phone number
   */
  recordSuccess(contact) {
    const contactKey = contact.toLowerCase()
    this.attempts.delete(contactKey)
    this.lockouts.delete(contactKey)
  }

  /**
   * Get remaining time for a locked contact
   * @param {string} contact - Email or phone number
   * @returns {number} - Remaining time in milliseconds
   */
  getRemainingLockoutTime(contact) {
    const contactKey = contact.toLowerCase()
    const lockout = this.lockouts.get(contactKey)
    if (!lockout) return 0
    
    const remaining = lockout.until - Date.now()
    return Math.max(0, remaining)
  }

  /**
   * Clear all rate limiting data (for testing or admin purposes)
   */
  clearAll() {
    this.attempts.clear()
    this.lockouts.clear()
  }

  /**
   * Get rate limiting status for a contact
   * @param {string} contact - Email or phone number
   * @returns {Object} - Current status
   */
  getStatus(contact) {
    const contactKey = contact.toLowerCase()
    const attempts = this.attempts.get(contactKey)
    const lockout = this.lockouts.get(contactKey)
    
    return {
      attempts: attempts || { count: 0, firstAttempt: 0, lockoutCount: 0 },
      lockout: lockout || null,
      isLocked: lockout && Date.now() < lockout.until
    }
  }
}

// Create a singleton instance
const rateLimiter = new RateLimiter()

export default rateLimiter
